import React, { useEffect, useMemo, useState } from "react";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { Building2, X } from "lucide-react";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { useCountries } from "@/hooks/useCountries";
import { profileService, LegalEntityHierarchy } from "@/services/profileService";
import { LogoUploadField } from "@/components/molecules/LogoUploadField";
import { normalizeLogoUrlForApi } from "@/lib/logoUpload";
import { resolvePhoneCodeOptionValue, sanitizeNationalPhoneInput } from "@/modules/contacts/contactFormUtils";
import { EMPTY_ENTITY_ADDRESS, EntityAddressFields } from "./EntityAddressFields";
import {
  extractCreatedEntityId,
  persistLegalEntityAddress,
  pickAddressValues,
  toLegalEntityRegisteredAddressPayload,
} from "./entityAddressUtils";

interface Props {
  callerEmail: string;
  legalEntities: LegalEntityHierarchy[];
  defaultParentId?: string;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

type FormErrors = Partial<Record<"companyName" | "email", string>>;

const EMPTY_FORM = (defaultParentId?: string) => ({
  companyName: "",
  parentLegalEntityId: defaultParentId ?? "",
  email: "",
  phoneDialCode: "+44",
  phoneCountryCode: "GB",
  phoneNumber: "",
  logoUrl: "",
  ...EMPTY_ENTITY_ADDRESS,
});

export const CreateLegalEntityDialog: React.FC<Props> = ({
  callerEmail,
  legalEntities,
  defaultParentId,
  open,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState(EMPTY_FORM(defaultParentId));
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const { phoneCodeOptions, loading: loadingCountries } = useCountries();

  const phoneCodeValue = useMemo(
    () => resolvePhoneCodeOptionValue(form.phoneDialCode, form.phoneCountryCode, phoneCodeOptions),
    [form.phoneDialCode, form.phoneCountryCode, phoneCodeOptions],
  );

  const flatEntities = useMemo(() => flattenEntities(legalEntities), [legalEntities]);
  const parentOptions = useMemo(
    () => [
      { value: "", label: "Your Main Company (Root)" },
      ...flatEntities.map((le) => ({ value: le.id, label: le.name, depth: le.depth })),
    ],
    [flatEntities],
  );

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM(defaultParentId));
    setErrors({});
  }, [open, defaultParentId]);

  const handleClose = () => {
    setForm(EMPTY_FORM(defaultParentId));
    setErrors({});
    onClose();
  };

  const { onOverlayClick } = useModalDismiss(open, handleClose);

  if (!open) return null;

  const setField = <K extends keyof typeof form>(key: K, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key in errors) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    if (!form.companyName.trim()) errs.companyName = "Company name is required.";
    const email = form?.email?.trim() || "";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = "Enter a valid email address.";
    }
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      const nationalPhone = form.phoneNumber.trim();
      const leEmail = form?.email?.trim() || null;
      const addressPayload = toLegalEntityRegisteredAddressPayload(pickAddressValues(form));

      const created = await profileService.createLegalEntity(callerEmail, {
        companyName: form.companyName.trim(),
        parentLegalEntityId: form.parentLegalEntityId || null,
        ...(leEmail ? { email: leEmail } : {}),
        logoUrl: normalizeLogoUrlForApi(form.logoUrl) || null,
        phoneNumber: nationalPhone ? `${form.phoneDialCode}${nationalPhone}` : null,
      });

      const createdId = extractCreatedEntityId(created);
      if (createdId) {
        const orgLegalEntityId = form.parentLegalEntityId || defaultParentId || undefined;
        await profileService.updateEntity(
          callerEmail,
          "LEGAL_ENTITY",
          createdId,
          {
            name: form.companyName.trim(),
            ...(leEmail ? { email: leEmail } : {}),
            phoneNumber: nationalPhone ? `${form.phoneDialCode}${nationalPhone}` : null,
            logoUrl: normalizeLogoUrlForApi(form.logoUrl) || null,
          },
          orgLegalEntityId,
        );
        await persistLegalEntityAddress(createdId, addressPayload);
      }

      onCreated();
      setForm(EMPTY_FORM(defaultParentId));
      setErrors({});
      onClose();
    } catch (err: any) {
      showActionErrorFromUnknown(err, "Failed to create legal entity");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-sm">
      <div
        className="flex min-h-full items-center justify-center px-4 py-16"
        onClick={onOverlayClick}
      >
        <div className="relative w-full max-w-3xl max-h-[calc(100vh-8rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-bridge-border/40">

          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-bridge-border/50 rounded-t-2xl bg-gradient-to-r from-bridge-panel to-slate-50/60">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#009BF5]/10 text-[#009BF5]">
                <Building2 size={18} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-bridge-fg leading-tight">Create Legal Entity</h2>
                <p className="text-[11px] text-bridge-muted mt-0.5 leading-tight">
                  Add contact details now or invite users later via Add Team Member.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="ml-3 shrink-0 flex h-7 w-7 items-center justify-center rounded-lg text-bridge-muted hover:text-bridge-fg hover:bg-bridge-surface transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <form
            id="create-legal-entity-form"
            onSubmit={handleSubmit}
            noValidate
            className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4 [scrollbar-gutter:stable]"
          >
            <Field
              label="Company Name"
              placeholder="Enter company name"
              value={form.companyName}
              onChange={(v) => setField("companyName", v)}
              error={errors.companyName}
              required
            />

            <LogoUploadField
              label="Legal Entity Logo"
              logoUrl={form.logoUrl}
              previewName={form.companyName}
              onChange={(logoUrl) => setField("logoUrl", logoUrl ?? "")}
              disabled={submitting}
            />

            <MenuSelect
              label="Parent Legal Entity"
              value={form.parentLegalEntityId}
              onChange={(v) => setField("parentLegalEntityId", v)}
              placeholder="Choose parent legal entity"
              options={parentOptions}
            />

            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Phone Number
              </label>
              <div className="flex gap-2">
                <div className="w-1/3">
                  <MenuSelect
                    options={phoneCodeOptions}
                    value={phoneCodeValue}
                    maxVisibleOptions={3}
                    onChange={(v) => {
                      const [nextDialCode, nextCountry] = v.split("|");
                      setForm((f) => ({
                        ...f,
                        phoneDialCode: nextDialCode,
                        phoneCountryCode: nextCountry || f.phoneCountryCode,
                      }));
                    }}
                    placeholder="Code"
                    loading={loadingCountries}
                    searchable
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={form.phoneNumber}
                    onChange={(e) => setField("phoneNumber", sanitizeNationalPhoneInput(e.target.value))}
                    placeholder="7123456789"
                    className="w-full py-2.5 px-4 bg-white border border-bridge-border/80 rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle shadow-sm outline-none transition-all focus:ring-2 focus:ring-[#009BF5]/20 focus:border-[#009BF5]"
                  />
                </div>
              </div>
            </div>

            <Field
              label="Contact Email"
              type="email"
              placeholder="Enter email address (optional)"
              value={form.email}
              onChange={(v) => setField("email", v)}
              error={errors.email}
            />

            <EntityAddressFields
              values={pickAddressValues(form)}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              disabled={submitting}
            />
          </form>

          {/* Footer */}
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-bridge-border/50 rounded-b-2xl bg-slate-50/60">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-bridge-muted hover:text-bridge-fg border border-bridge-border/70 rounded-xl hover:bg-white transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-legal-entity-form"
              disabled={submitting}
              className="px-5 py-2 text-sm font-bold text-white bg-[#009BF5] hover:bg-[#0085d4] rounded-xl shadow-sm disabled:opacity-50 transition-colors"
            >
              {submitting ? "Creating…" : "Create Legal Entity"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
}> = ({ label, value, onChange, type = "text", placeholder, required, error }) => {
  const hasError = Boolean(error);

  return (
    <div>
      <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
        {label}
        {required ? <span className="ml-0.5 text-red-400">*</span> : null}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={hasError}
        className={`w-full py-2.5 px-4 bg-white border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle shadow-sm outline-none transition-all ${
          hasError
            ? "border-red-400 focus:ring-2 focus:ring-red-400/20 focus:border-red-400"
            : "border-bridge-border/80 focus:ring-2 focus:ring-[#009BF5]/20 focus:border-[#009BF5]"
        }`}
      />
      {hasError && (
        <p className="mt-1.5 text-xs text-red-500 font-medium">{error}</p>
      )}
    </div>
  );
};

function flattenEntities(
  entities: LegalEntityHierarchy[],
  depth = 0,
): (LegalEntityHierarchy & { depth: number })[] {
  return entities.reduce(
    (acc, le) => {
      acc.push({ ...le, depth });
      if (le.subEntities && le.subEntities.length > 0) {
        acc.push(...flattenEntities(le.subEntities, depth + 1));
      }
      return acc;
    },
    [] as (LegalEntityHierarchy & { depth: number })[],
  );
}
